import { useEffect, useRef, useState, type FormEvent } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";

interface LoginFormProps {
  client: SupabaseClient;
}

const RESEND_COOLDOWN_SECONDS = 60;

// リロードしてもコード入力ステップを維持するための sessionStorage 記録。
// OTP の有効期限（既定1時間）を過ぎた記録は復元しない。
const OTP_SESSION_KEY = "milledon-admin-otp";
const OTP_SESSION_MAX_AGE_MS = 60 * 60 * 1000;

type OtpSession = { email: string; sentAt: number };

function loadOtpSession(): OtpSession | null {
  try {
    const raw = sessionStorage.getItem(OTP_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OtpSession>;
    if (typeof parsed.email !== "string" || typeof parsed.sentAt !== "number")
      return null;
    if (Date.now() - parsed.sentAt > OTP_SESSION_MAX_AGE_MS) {
      sessionStorage.removeItem(OTP_SESSION_KEY);
      return null;
    }
    return { email: parsed.email, sentAt: parsed.sentAt };
  } catch {
    return null;
  }
}

export function LoginForm({ client }: LoginFormProps) {
  const [restored] = useState(loadOtpSession);
  const [step, setStep] = useState<"email" | "code">(
    restored ? "code" : "email",
  );
  const [email, setEmail] = useState(restored?.email ?? "");
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (restored) {
      const elapsed = Math.floor((Date.now() - restored.sentAt) / 1000);
      const remaining = RESEND_COOLDOWN_SECONDS - elapsed;
      if (remaining > 0) startCooldown(remaining);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウント時の復元のみ
  }, []);

  function startCooldown(seconds: number = RESEND_COOLDOWN_SECONDS) {
    setCooldown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((current) => {
        if (current <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }

  async function sendCode() {
    setErrorMessage(null);
    setIsSubmitting(true);

    const { error } = await client.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });

    setIsSubmitting(false);

    if (error) {
      setErrorMessage(
        error.status === 400
          ? "メールアドレスを確認してください。"
          : "認証コードを送信できませんでした。しばらくしてから再試行してください。",
      );
      return;
    }

    try {
      sessionStorage.setItem(
        OTP_SESSION_KEY,
        JSON.stringify({ email, sentAt: Date.now() } satisfies OtpSession),
      );
    } catch {
      // 保存できなくてもログイン自体は続行できる
    }
    setStep("code");
    startCooldown();
  }

  async function handleEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await sendCode();
  }

  async function handleCodeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    const { error } = await client.auth.verifyOtp({
      email,
      token: code,
      type: "email",
    });

    if (error) {
      setErrorMessage(
        "認証コードが正しくないか、有効期限が切れています。再送信してお試しください。",
      );
    } else {
      sessionStorage.removeItem(OTP_SESSION_KEY);
    }

    setIsSubmitting(false);
  }

  function backToEmail() {
    sessionStorage.removeItem(OTP_SESSION_KEY);
    setStep("email");
    setCode("");
    setErrorMessage(null);
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-12">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <p className="text-xs font-medium tracking-[0.2em] text-muted-foreground uppercase">
            milledon
          </p>
          <CardTitle className="text-xl">管理画面にログイン</CardTitle>
          <CardDescription>
            {step === "email"
              ? "管理者として登録されたアカウントを使用してください。"
              : `${email} に認証コードを送信しました。`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "email" ? (
            <form className="grid gap-5" onSubmit={handleEmailSubmit}>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <div className="grid gap-2">
                <Label htmlFor="email">メールアドレス</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? "送信中…" : "コードを送信"}
              </Button>
            </form>
          ) : (
            <form className="grid gap-5" onSubmit={handleCodeSubmit}>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <div className="grid gap-2">
                <Label htmlFor="code">認証コード</Label>
                <Input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6,8}"
                  maxLength={8}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {isSubmitting ? "確認中…" : "ログイン"}
              </Button>
              <div className="grid gap-2 text-center text-sm text-muted-foreground">
                <button
                  type="button"
                  className="underline underline-offset-2 disabled:text-muted-foreground/50"
                  onClick={sendCode}
                  disabled={isSubmitting || cooldown > 0}
                >
                  {cooldown > 0
                    ? `認証コードを再送信（${cooldown}秒後に再送信可能）`
                    : "認証コードを再送信"}
                </button>
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={backToEmail}
                >
                  メールアドレスを変更
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
